import { useState, useRef, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { authService, extractDevOtpFromLoginPayload, extractOtpBypassFromLoginPayload } from '@/services';
import { defaultAuthedPath } from '@/utils/authPaths';
import { getFingerprint, getIamAccessToken } from '@/api';
import {
  Mail, Lock, Eye, EyeOff, Truck, FileCheck, Shield,
  Loader2, ArrowLeft, ShieldCheck,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '@/assets/logo.png';
import { cn } from '@/utils/cn';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';

/* ─── Schemas ─── */
const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  rememberMe: z.boolean().optional(),
});

type LoginForm = z.infer<typeof loginSchema>;

/* ─── Styles & Constants ─── */
const BRAND = '#DC2925';
const BRAND_DARK = '#8B0000';

/* ITSM-aligned OTP (Login.tsx MFA screen): 6 cells, form submit, no auto-verify */
const OTP_LENGTH = 6;

function OTPScreen({
  email,
  successMsg,
  serverError,
  onBack,
  onVerify,
  isLoading,
  devPrefillOtp,
  otpBypassAutofill,
}: {
  email: string;
  successMsg?: string | null;
  serverError?: string | null;
  onBack: () => void;
  onVerify: (otp: string) => void;
  isLoading: boolean;
  /** 6-digit OTP from Auth Utils login response (e.g. otp_bypass or dev echo fields) */
  devPrefillOtp?: string | null;
  /** IAM returned otp_bypass — show hint even outside Vite dev */
  otpBypassAutofill?: boolean;
}) {
  const { t } = useTranslation('Login');
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    setTimeout(() => otpRefs.current[0]?.focus(), 100);
  }, []);

  useEffect(() => {
    if (!devPrefillOtp || devPrefillOtp.length !== OTP_LENGTH) return;
    const digits = devPrefillOtp.replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (digits.length !== OTP_LENGTH) return;
    setOtp(digits.split(''));
    setTimeout(() => otpRefs.current[OTP_LENGTH - 1]?.focus(), 80);
  }, [devPrefillOtp]);

  useEffect(() => {
    if (!serverError) return;
    setOtp(Array(OTP_LENGTH).fill(''));
    setTimeout(() => otpRefs.current[0]?.focus(), 50);
  }, [serverError]);

  const handleOtpChange = (index: number, value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(-1);
    const updated = [...otp];
    updated[index] = cleaned;
    setOtp(updated);
    if (cleaned && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        otpRefs.current[index - 1]?.focus();
        const updated = [...otp];
        updated[index - 1] = '';
        setOtp(updated);
      } else {
        const updated = [...otp];
        updated[index] = '';
        setOtp(updated);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const updated = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => {
      updated[i] = ch;
    });
    setOtp(updated);
    const nextFocus = Math.min(pasted.length, OTP_LENGTH - 1);
    otpRefs.current[nextFocus]?.focus();
  };

  const verifyUserOtp = (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join('');
    if (otpValue.length !== OTP_LENGTH) return;
    onVerify(otpValue);
  };

  return (
    <motion.div
      key="otp"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
      className="space-y-6 w-full"
    >
      <button
        type="button"
        onClick={() => {
          setOtp(Array(OTP_LENGTH).fill(''));
          onBack();
        }}
        disabled={isLoading}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-2"
      >
        <ArrowLeft className="w-4 h-4" /> {t('otp.backToLogin', 'Back to login')}
      </button>

      <div className="flex flex-col items-center text-center space-y-3">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-2 border-2"
          style={{ background: '#FDECEA', borderColor: BRAND }}
        >
          <ShieldCheck className="w-8 h-8" style={{ color: BRAND }} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{t('otp.title', 'Two-Step Verification')}</h2>
        <p className="text-sm text-gray-500">
          {t('otp.description', 'We sent a 6-digit code to')} <br />
          <b className="text-gray-900">{email || 'your email'}</b>
        </p>
        {(otpBypassAutofill || (import.meta.env.DEV && devPrefillOtp)) && devPrefillOtp ? (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-sm">
            {otpBypassAutofill
              ? t('otp.otpBypassWarning', 'OTP from IAM (otp_bypass) was filled automatically. Remove otp_bypass from IAM responses in production.')
              : t('otp.devOtpFilled', 'Dev: OTP from login response is pre-filled. Disable with')}{' '}
            {!otpBypassAutofill ? <code className="text-[11px]">VITE_DEV_AUTO_OTP=false</code> : null}
          </p>
        ) : null}
      </div>

      {successMsg ? (
        <div className="p-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg">
          {t('otp.successMessage', successMsg)}
        </div>
      ) : null}

      {serverError ? (
        <div className="p-3 text-sm text-center text-red-600 bg-red-50 border border-red-200 rounded-lg">{serverError}</div>
      ) : null}

      <form onSubmit={verifyUserOtp} className="space-y-6">
        <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
          {otp.map((digit, index) => (
            <motion.input
              key={index}
              ref={(el) => {
                otpRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(index, e)}
              whileFocus={{ scale: 1.05 }}
              disabled={isLoading}
              className={cn(
                'w-11 h-14 text-center text-xl font-bold rounded-lg border-2 bg-gray-50 transition-all',
                'focus:outline-none focus:ring-2 focus:ring-[#DC2925] focus:border-[#DC2925]',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                serverError
                  ? 'border-red-500 text-red-600 bg-red-50'
                  : digit
                    ? 'border-[#DC2925] text-[#DC2925]'
                    : 'border-gray-200 text-gray-900'
              )}
            />
          ))}
        </div>

        <button
          type="submit"
          disabled={isLoading || otp.join('').length !== OTP_LENGTH}
          className="w-full bg-[#DC2925] text-white py-3 rounded-lg font-semibold hover:bg-[#B71C1C] disabled:opacity-50 transition-colors shadow-md flex justify-center items-center gap-2"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Shield className="w-5 h-5" />}
          {isLoading ? t('otp.verifying', 'Signing in…') : t('otp.verifyButton', 'Sign in')}
        </button>
      </form>
    </motion.div>
  );
}

function BrandPanel() {
  const { t } = useTranslation('Login');
  const features = [
    {
      icon: Truck,
      title: t('brand.features.0.title', 'Proof of delivery, end to end'),
      desc: t('brand.features.0.desc', 'Upload and track POD documents with SAP-aligned flows (ALT, DHR, GRN, and more).'),
    },
    {
      icon: FileCheck,
      title: t('brand.features.1.title', 'Approvals that scale'),
      desc: t('brand.features.1.desc', 'Requesters, approvers, and transporters each see the right queue and status.'),
    },
    {
      icon: Shield,
      title: t('brand.features.2.title', 'IAM-secured access'),
      desc: t('brand.features.2.desc', 'Same LEAP identity and MFA you use across the suite — sign in once, trust everywhere.'),
    },
  ];

  return (
    <div
      className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-center px-20 text-white"
      style={{ background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)` }}
    >
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="podLoginDotGrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.5" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#podLoginDotGrid)" />
        </svg>
      </div>

      <div className="relative z-10 w-full max-w-lg">
        <div className="flex items-center gap-4 mb-12 brand-entrance">
          <div className="h-16 w-auto">
            <img src={logo} alt="LEAP India" className="h-full object-contain brightness-0 invert" />
          </div>
          <div className="w-px h-10 bg-white/20" />
          <div>
            <p className="font-extrabold text-2xl tracking-tighter leading-none">{t('header.appName', 'POD System')}</p>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/50 mt-1">
              {t('header.appSubtitle', 'Proof of Delivery')}
            </p>
          </div>
        </div>

        <h1 className="text-6xl font-black mb-8 tracking-tight leading-[1.05] brand-entrance">
          {t('brand.title', 'Ship with confidence.').replace(t('brand.subtitle', 'confidence.'), '')} <br />
          <span className="text-white/60">{t('brand.subtitle', 'confidence.')}</span>
        </h1>

        <div className="space-y-8 mt-16">
          {features.map((f, i) => (
             <div 
              key={f.title} 
              className="flex items-start gap-5 brand-entrance" 
              style={{ animationDelay: `${0.2 + i * 0.1}s` }}
            >
              <div className="w-12 h-12 bg-white/15 backdrop-blur-md rounded-2xl flex items-center justify-center flex-shrink-0 border border-white/10">
                <f.icon size={22} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">{f.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { t } = useTranslation('Login');
  const navigate = useNavigate();
  const { login, resetAuth, setLoading, setError, isLoading, error } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [screen, setScreen] = useState<'login' | 'otp'>('login');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [otpSuccessMsg, setOtpSuccessMsg] = useState<string | null>(null);
  const [devPrefillOtp, setDevPrefillOtp] = useState<string | null>(null);
  const [otpBypassAutofill, setOtpBypassAutofill] = useState(false);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    setForgotMessage(null);
    try {
      const { authApi } = await import('@/api/services');
      const res = await authApi.forgotPassword(forgotEmail);
      if (res.status === 'success') {
        setForgotMessage({
          type: 'success',
          text: res.message || 'Password reset link sent to your email',
        });
      } else {
        setForgotMessage({
          type: 'error',
          text: res.message || 'Email address not found',
        });
      }
    } catch (err: any) {
      setForgotMessage({
        type: 'error',
        text: err.response?.data?.message || err.message || 'Failed to request password reset',
      });
    } finally {
      setForgotLoading(false);
    }
  };

  useEffect(() => {
    const fp = getFingerprint();
    const jwt = getIamAccessToken();
    if (useAuthStore.getState().isAuthenticated && (!fp || !jwt)) {
      resetAuth();
    }
  }, [resetAuth]);

  /** Prefill from echoed OTP fields when Vite dev (unless disabled) or when VITE_DEV_AUTO_OTP=true */
  const devAutoOtp =
    import.meta.env.VITE_DEV_AUTO_OTP === 'true' ||
    (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTO_OTP !== 'false');

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { rememberMe: false },
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      resetAuth();
      setLoading(true);
      setError(null);

      const loginPayload = await authService.login(data.email, data.password);

      setSubmittedEmail(data.email);
      const bypass = extractOtpBypassFromLoginPayload(loginPayload);
      setOtpBypassAutofill(Boolean(bypass));
      const fromApiTree = extractDevOtpFromLoginPayload(loginPayload);
      const mockRaw = import.meta.env.VITE_DEV_MOCK_OTP;
      const fromMock =
        typeof mockRaw === 'string' && /^\d{6}$/.test(mockRaw) ? mockRaw : null;
      /** otp_bypass always wins when present (IAM non-prod); other echoes only when devAutoOtp */
      const prefill = bypass ?? (devAutoOtp ? (fromApiTree ?? fromMock) : null);
      setDevPrefillOtp(prefill);
      if (import.meta.env.DEV && devAutoOtp && !bypass && !fromApiTree && !fromMock) {
        console.info(
          '[POD] Dev auto-OTP: no OTP in login JSON. ' +
            'Use IAM otp_bypass, or set VITE_DEV_MOCK_OTP=______ in .env (6 digits).'
        );
      }
      setOtpSuccessMsg("We've sent a 6-digit code to your registered email.");
      setError(null);
      setScreen('otp');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = useCallback(
    async (code: string) => {
      try {
        setLoading(true);
        setError(null);

        if (code.length !== 6) {
          setError('Invalid code');
          return;
        }

        await authService.verifyOtp(code);
        const me = await authService.getCurrentUser();
        if (!me.success) {
          setError('Failed to load user profile.');
          return;
        }

        if (!me.data) {
          setError('Failed to load user profile.');
          return;
        }
        login(me.data);
        setOtpSuccessMsg(null);
        navigate(defaultAuthedPath(me.data));
      } catch (err: unknown) {
        const msg =
          err instanceof Error
            ? err.message
            : err &&
                typeof err === 'object' &&
                'message' in err &&
                typeof (err as { message?: unknown }).message === 'string'
              ? String((err as { message: string }).message)
              : 'Sign-in failed. Check the OTP and try again.';
        setError(msg || 'Sign-in failed. Check the OTP and try again.');
      } finally {
        setLoading(false);
      }
    },
    [login, navigate, setLoading, setError]
  );

  const inputBase =
    'w-full pl-11 pr-4 py-3 rounded-xl border-2 text-sm font-medium outline-none transition-all duration-200 bg-gray-50 text-gray-900 placeholder:text-gray-400';
  const inputStyle = (hasError: boolean) => ({
    borderColor: hasError ? BRAND : '#E0E0E0',
    boxShadow: hasError ? `0 0 0 3px rgba(220,41,37,0.12)` : 'none',
  });
  const inputFocusStyle = { borderColor: BRAND, boxShadow: `0 0 0 3px rgba(220,41,37,0.12)` };

  return (
    <>
      <style>{`
        @keyframes brandEntrance {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .brand-entrance {
          opacity: 0;
          animation: brandEntrance 0.55s cubic-bezier(0.22,1,0.36,1) forwards;
        }
        @keyframes formEntrance {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .form-entrance {
          animation: formEntrance 0.5s cubic-bezier(0.22,1,0.36,1) forwards;
        }
      `}</style>

      <div className="flex min-h-screen relative" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="absolute top-4 right-4 z-50">
          <LanguageSwitcher />
        </div>
        <BrandPanel />

        <div className="flex-1 flex items-center justify-center px-4 py-12 sm:px-8">
          <div className="w-full max-w-lg">
            <div className="flex flex-col items-center mb-8 lg:hidden form-entrance">
              <div className="h-16 w-auto mb-4">
                <img src={logo} alt="LEAP India" className="h-full object-contain" />
              </div>
              <p className="font-extrabold text-gray-900 text-lg leading-none">{t('header.appName', 'POD System')}</p>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400 mt-1">
                {t('header.appSubtitle', 'Proof of Delivery')}
              </p>
            </div>

            <div
              className="bg-white rounded-2xl shadow-xl border border-gray-100 p-10 form-entrance"
              style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}
            >
              <AnimatePresence mode="wait">
                {screen === 'login' ? (
                  <motion.div
                    key="login"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="mb-8">
                      <h2 className="text-3xl font-bold text-gray-900">{t('form.welcomeBack', 'Welcome back')}</h2>
                      <p className="text-sm text-gray-500 mt-1.5">
                        {t('form.welcomeSubtitle', 'Sign in to POD with your LEAP IAM email and password')}
                      </p>
                    </div>

                    {error && (
                      <div
                        className="flex items-start gap-2.5 rounded-xl px-4 py-3 text-sm mb-5"
                        style={{ backgroundColor: '#FDECEA', border: `1px solid #F1948A` }}
                      >
                        <span className="mt-0.5">❌</span>
                        <p className="text-red-800">{error}</p>
                      </div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          {t('form.emailLabel', 'Email address')}
                        </label>
                        <div className="relative">
                          <Mail
                            size={16}
                            className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
                            style={{ color: errors.email ? BRAND : '#9CA3AF' }}
                          />
                          <input
                            {...register('email')}
                            type="email"
                            autoComplete="email"
                            placeholder={t('form.emailPlaceholder', 'you@company.com')}
                            disabled={isLoading}
                            className={inputBase}
                            style={
                              errors.email
                                ? { ...inputStyle(true), borderColor: BRAND }
                                : inputStyle(false)
                            }
                            onFocus={(e) => { if (!errors.email) Object.assign(e.target.style, inputFocusStyle); }}
                            onBlur={(e) => { if (!errors.email) Object.assign(e.target.style, { borderColor: '#E0E0E0', boxShadow: 'none' }); }}
                          />
                        </div>
                        {errors.email && (
                          <p className="text-xs mt-1.5 font-medium" style={{ color: BRAND }}>
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          {t('form.passwordLabel', 'Password')}
                        </label>
                        <div className="relative">
                          <Lock
                            size={16}
                            className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
                            style={{ color: errors.password ? BRAND : '#9CA3AF' }}
                          />
                          <input
                            {...register('password')}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            placeholder={t('form.passwordPlaceholder', '••••••••')}
                            disabled={isLoading}
                            className={`${inputBase} pr-11`}
                            style={
                              errors.password
                                ? { ...inputStyle(true), borderColor: BRAND }
                                : inputStyle(false)
                            }
                            onFocus={(e) => { if (!errors.password) Object.assign(e.target.style, inputFocusStyle); }}
                            onBlur={(e) => { if (!errors.password) Object.assign(e.target.style, { borderColor: '#E0E0E0', boxShadow: 'none' }); }}
                          />
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 p-0.5 rounded transition-colors hover:opacity-60"
                            style={{ color: '#9CA3AF' }}
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        {errors.password && (
                          <p className="text-xs mt-1.5 font-medium" style={{ color: BRAND }}>
                            {errors.password.message}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            {...register('rememberMe')}
                            type="checkbox"
                            className="w-4 h-4 rounded"
                            style={{ accentColor: BRAND }}
                          />
                          <span className="text-sm text-gray-600">{t('form.rememberMe', 'Remember me')}</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setForgotOpen(true)}
                          className="text-sm font-semibold transition-opacity hover:opacity-70"
                          style={{ color: BRAND }}
                        >
                          {t('form.forgotPasswordLink', 'Forgot password?')}
                        </button>
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-2.5 py-4 rounded-xl text-white font-semibold text-base transition-all duration-200 mt-2"
                        style={{
                          background: isLoading
                            ? '#ccc'
                            : `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
                          boxShadow: isLoading
                            ? 'none'
                            : `0 8px 30px rgba(220,41,37,0.3)`,
                          cursor: isLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            {t('form.signingIn', 'Signing in…')}
                          </>
                        ) : (
                          <>
                            <Lock size={16} />
                            {t('form.loginButton', 'Sign in securely')}
                          </>
                        )}
                      </button>
                    </form>

                    <div className="relative my-7">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-100" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-white px-4 text-xs text-gray-400 font-medium">
                          {t('footer.brandName', 'LEAP · POD System')}
                        </span>
                      </div>
                    </div>

                    <p className="text-center text-xs text-gray-400">
                      {t('footer.agreement', 'By signing in you agree to our')}{' '}
                      <button className="underline hover:opacity-70" style={{ color: BRAND }}>
                        {t('footer.termsOfService', 'Terms of Service')}
                      </button>{' '}
                      {t('footer.and', 'and')}{' '}
                      <button className="underline hover:opacity-70" style={{ color: BRAND }}>
                        {t('footer.privacyPolicy', 'Privacy Policy')}
                      </button>.
                    </p>
                  </motion.div>
                ) : (
                  <OTPScreen
                    key={`otp-${submittedEmail}-${devPrefillOtp ?? ''}`}
                    email={submittedEmail}
                    successMsg={otpSuccessMsg}
                    serverError={error}
                    onBack={() => {
                      setScreen('login');
                      setError(null);
                      setOtpSuccessMsg(null);
                      setDevPrefillOtp(null);
                      setOtpBypassAutofill(false);
                    }}
                    onVerify={handleOTPVerify}
                    isLoading={isLoading}
                    devPrefillOtp={devPrefillOtp}
                    otpBypassAutofill={otpBypassAutofill}
                  />
                )}
              </AnimatePresence>
            </div>

            <p className="text-center text-xs text-gray-400 mt-6">
              {t('footer.copyright', '© {{year}} LEAP India — Proof of Delivery (POD)', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {forgotOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full relative border border-gray-100"
            >
              <button
                type="button"
                onClick={() => {
                  setForgotOpen(false);
                  setForgotEmail('');
                  setForgotMessage(null);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>

              <div className="flex flex-col items-center text-center space-y-4 mb-6">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: '#FDECEA' }}
                >
                  <Mail className="w-6 h-6" style={{ color: BRAND }} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Reset Password</h3>
                <p className="text-sm text-gray-500">
                  Enter your email address and we'll request a password reset link from IAM.
                </p>
              </div>

              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-gray-400">
                      <Mail size={16} />
                    </span>
                    <input
                      type="email"
                      required
                      placeholder="user@example.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all text-gray-900"
                      disabled={forgotLoading}
                    />
                  </div>
                </div>

                {forgotMessage && (
                  <div
                    className={cn(
                      "p-3 rounded-lg text-xs font-medium text-center border",
                      forgotMessage.type === 'success'
                        ? "bg-green-50 border-green-200 text-green-700"
                        : "bg-red-50 border-red-200 text-red-700"
                    )}
                  >
                    {forgotMessage.text}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-sm transition-all duration-200"
                  style={{
                    background: forgotLoading
                      ? '#ccc'
                      : `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
                    cursor: forgotLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {forgotLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Request Reset Link'
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
